import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ScanLine, CheckCircle2, Globe } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import TryScanner from '../components/TryScanner';
import RelatedLinks from '../components/RelatedLinks';
import ProductBridge from '../components/ProductBridge';
import PageGuide from '../components/PageGuide';
import { SCANNER_PAGES, SCANNER_LIST } from '../data/scannerPages';

export default function ScannerLanding({ slug }) {
  const data = SCANNER_PAGES[slug];
  if (!data) return <Navigate to="/invoice-scanner" replace />;

  const path = `/${data.slug}`;
  const others = SCANNER_LIST.filter((p) => p.slug !== data.slug);
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: data.h1, url: `https://moneyfeed.io${path}`, description: data.seoDescription },
    { '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: `MoneyFeed — ${data.badge}`, applicationCategory: 'FinanceApplication', operatingSystem: 'Web, iOS, Android', offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' } },
    { '@context': 'https://schema.org', '@type': 'HowTo', name: data.h1, step: data.steps.map((s, i) => ({ '@type': 'HowToStep', position: i + 1, text: s })) },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: data.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid={`scanner-landing-${data.slug}`}>
      <Seo title={data.seoTitle} description={data.seoDescription} path={path} jsonLd={jsonLd} />
      <LandingHeader />

      <section className="max-w-5xl mx-auto px-4 sm:px-8 pt-14 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ backgroundColor: `${data.accent}1A` }}>
          <ScanLine className="h-4 w-4" style={{ color: data.accent }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: data.accent }}>{data.badge}</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">
          {data.h1.replace(data.hi, '')}<span style={{ color: data.accent }}>{data.hi}</span>
        </h1>
        <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">{data.intro}</p>
        {data.esHref && (
          <Link to={data.esHref} data-testid="scanner-es-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16]">
            <Globe className="h-4 w-4" /> Español
          </Link>
        )}
      </section>

      <section className="max-w-2xl mx-auto px-4 sm:px-8 pb-10">
        <TryScanner format={data.format} />
      </section>

      <PageGuide />

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-8">How it works</h2>
        <div className="space-y-3 max-w-2xl mx-auto">
          {data.steps.map((s, i) => (
            <div key={i} className="flex items-start gap-3 bg-white rounded-2xl shadow-sm p-4" data-testid={`scanner-step-${i}`}>
              <span className="w-7 h-7 rounded-full bg-[#84CC16] text-black text-sm font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
              <p className="text-gray-700 text-sm pt-0.5">{s}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-8">FAQ</h2>
        <div className="space-y-4">
          {data.faqs.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`scanner-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Scan every receipt in one place</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Bulk-scan, auto-categorize, and export to Excel, CSV or PDF — with tax-ready reports built in. Free to start.</p>
          <Link to="/auth">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-8 py-6 text-base" data-testid="scanner-cta-signup">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <ProductBridge intent={/invoice/.test(data.slug) ? 'invoice' : 'receipt'} />

      <section className="max-w-4xl mx-auto px-4 sm:px-8 pb-8"><Link to="/scan-center" className="block bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center hover:shadow-md transition"><p className="text-sm font-semibold text-[#064E3B]">Do this automatically for every receipt — meet MoneyFeed Scan Center</p><p className="text-gray-600 text-sm mt-2">Snap or send receipts on WhatsApp or in the app and MoneyFeed reads, categorizes and files them into clean books.</p><span className="inline-block mt-3 text-sm font-semibold text-[#0D9488]">Explore Scan Center →</span></Link></section>
      <RelatedLinks exclude={[`/${data.slug}`]} />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/invoice-scanner" className="hover:text-[#064E3B]">Invoice Scanner</Link>
          {others.map((o) => (
            <Link key={o.slug} to={`/${o.slug}`} className="hover:text-[#064E3B]">{o.badge}</Link>
          ))}
        </div>
      </footer>
    </div>
  );
}
